<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import { computed, ref, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { FormFlattenKey, FormFlattenValue, FormResult } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOptionWithExtendedProps } from '@modules/forms/types';
import type { IPaymentSystemFormSchema, IPaymentSystemsFields, IRelatedPaymentRequestItem, PaymentSystemName } from '@modules/payment-system-details/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import VHorizontalScrollNav from '@components/scroll/VHorizontalScrollNav/VHorizontalScrollNav.vue';
import { useModalPageWrapper } from '@modules/core/components/ModalPageWrapper/composables';
import { useFormatDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import AffiliatePaymentSystemDialog from '@modules/payment-system-details/components/AffiliatePaymentSystemDialog/AffiliatePaymentSystemDialog.vue';
import VReviewPaymentSystemButtons from '@modules/payment-system-details/components/VReviewPaymentSystemButtons/VReviewPaymentSystemButtons.vue';
import VReviewPaymentSystemInfo from '@modules/payment-system-details/components/VReviewPaymentSystemInfo/VReviewPaymentSystemInfo.vue';
import { useConfirmPaymentSystem, useCurrentPaymentSystem, usePaymentSystemForm } from '@modules/payment-system-details/composables';
import {
  ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_ROUTE_NAME,
  ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_ROUTE_NAME,
  PAYMENT_SYSTEM_DETAILS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS,
} from '@modules/payment-system-details/constants';
import { useAdminChangePaymentSystemMutation } from '@modules/payment-system-details/mutations';
import { useAdminPaymentSystemShowQuery } from '@modules/payment-system-details/queries';
import { EPaymentSystemStatus, EReviewPaymentSystemActionType, isPaymentSystemName } from '@modules/payment-system-details/types';
import { useNamespaceRoutes } from '@modules/permissions/composables';
import { isString } from '@utils/guards';

import type { PaymentSystemDetailsViewPageProps } from './types';

const props = defineProps<PaymentSystemDetailsViewPageProps>();

const selectedPaymentSystemName = ref<PaymentSystemName>();
const isEditableMode = ref(false);
const rejectionReason = toRef(props, 'rejectionReason');

const router = useRouter();
const route = useRoute();
const { getTranslation } = useI18n();
const { showPageDialog, closePageDialog } = useModalPageWrapper();
const { isCurrentRouteChildOf } = useNamespaceRoutes();
const { getPaymentSystem } = useCurrentPaymentSystem();
const { formatDate } = useFormatDate();

const { onConfirmSingle, isSingleConfirmLoading } = useConfirmPaymentSystem(rejectionReason, closePageDialog);

const pendingListRoute = { name: ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS_LIST_ROUTE_NAME };
const historyListRoute = { name: ADMIN_PAYMENT_SYSTEM_DETAILS_REQUESTS_HISTORY_LIST_ROUTE_NAME };

const backToRoute = computed<RouteLocationRaw>(() => isCurrentRouteChildOf(pendingListRoute) ? pendingListRoute : historyListRoute);

const paymentSystemId = computed<number>(() => isString(route.params.id) ? Number(route.params.id) : 0);

const { data, isLoading: isShowLoading } = useAdminPaymentSystemShowQuery(paymentSystemId);

const relatedRequestsTabs = computed<IRelatedPaymentRequestItem[]>(() => {
  if (!data.value) {
    return [];
  }

  return (data.value.related_requests ?? [])
    .map((item) => ({ ...item, id: Number(item.id), created_at: formatDate(item.created_at, 'P') }))
    .sort((a, b) => b.id - a.id);
});

const activeTabIndex = computed<number | undefined>(() => relatedRequestsTabs.value?.findIndex(({ id }) => id === paymentSystemId.value));

const {
  mutateAsync,
  isLoading: isSaveLoading,
  errors,
  clearError,
  clearAllErrors,
} = useAdminChangePaymentSystemMutation();

const isReviewed = computed<boolean>(() => data.value?.payment_change_form?.status === EPaymentSystemStatus.REJECTED || data.value?.payment_change_form?.status === EPaymentSystemStatus.APPROVED);

const newPaymentMethod = computed(() => data.value?.payment_change_form?.new_payment_method);
const oldPaymentMethod = computed(() => data.value?.payment_change_form?.old_payment_method);

const newPaymentMethodName = computed(() => selectedPaymentSystemName.value || newPaymentMethod.value?.name);
const oldPaymentMethodName = computed(() => oldPaymentMethod.value?.name);

const {
  paymentSystemSchema: newPaymentSystemSchema,
  paymentSettingsForm,
  paymentSystemsNamesList,
  isDataLoading,
  changeCurrentFormValues,
  onFormChange,
  onSubmit,
} = usePaymentSystemForm(newPaymentMethodName, newPaymentMethod);

const { paymentSystemSchema: oldPaymentSystemSchema } = usePaymentSystemForm(oldPaymentMethodName, oldPaymentMethod, () => !isReviewed.value);

const isLoading = computed(() => isShowLoading.value || isDataLoading.value);

const paymentSystemNameOptions = computed<IFormSelectInputOptionWithExtendedProps<PaymentSystemName>[]>(() => {
  return paymentSystemsNamesList.value.map((item) => ({
    value: item,
    label: item,
    image: getPaymentSystem(item)?.icon,
  }));
});

const newPaymentSystemFormSchema = computed<IPaymentSystemFormSchema>(() => ({
  name: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'FORM_PAYMENT_SYSTEM_NAME_LABEL').value,
    options: paymentSystemNameOptions.value,
    readonly: isReviewed.value,
    required: true,
    value: newPaymentMethodName.value,
  },
  ...newPaymentSystemSchema.value,
}));

const oldPaymentSystemFormSchema = computed<IPaymentSystemFormSchema>(() => ({
  name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'FORM_PAYMENT_SYSTEM_NAME_LABEL').value,
    value: oldPaymentMethodName.value as string,
    image: getPaymentSystem(oldPaymentMethodName.value)?.icon,
  },
  ...oldPaymentSystemSchema.value,
}));

function onChange<
  T extends IPaymentSystemsFields,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
  result: FormResult<IPaymentSystemsFields>,
): void {
  if (key === 'name' && isPaymentSystemName(value)) {
    selectedPaymentSystemName.value = value;
  } else {
    onFormChange(key, value, result);
  }
}

async function onSave(values: IPaymentSystemsFields): Promise<void> {
  try {
    await mutateAsync({ id: paymentSystemId.value, payment_system: values });

    isEditableMode.value = false;
  } catch {}
}

function setEditableMode(value: boolean): void {
  isEditableMode.value = value;

  if (!isEditableMode.value) {
    selectedPaymentSystemName.value = undefined;
    changeCurrentFormValues(newPaymentMethod.value);
    clearAllErrors();
  }
}

function onTabUpdate(id: string | number) {
  void router.push({
    name: route.name,
    params: {
      ...route.params,
      id,
    },
  });
}
</script>

<template>
  <AffiliatePaymentSystemDialog v-data-qa-auto-id="'PaymentSystemDetailsViewPage'"
    v-model:visible="showPageDialog"
    :title="$getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'REVIEW_MODAL_HEADER')"
    :back-to-route="backToRoute"
    sticky-sides
    sticky-footer
  >
    <template #info>
      <div>
        <VHorizontalScrollNav
          :active-index="activeTabIndex"
          element-class="p-tab"
          class="mb-4 min-h-14"
          full-border-width
        >
          <PTabs
            v-if="relatedRequestsTabs?.length"
            :value="paymentSystemId"
            @update:value="onTabUpdate"
          >
            <PTabList>
              <PTab
                v-for="tab in relatedRequestsTabs"
                :key="tab.id"
                :value="tab.id"
              >
                {{ $getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'REVIEW_TAB_NAME', {
                  defaultValue: `ID: ${tab.id} | ${tab.created_at}`,
                  replace: {
                    id: tab.id,
                    created_at: tab.created_at,
                  },
                }) }}
              </PTab>
            </PTabList>
          </PTabs>
        </VHorizontalScrollNav>
        <VLoader
          :loading="isLoading"
          min-height="150px"
        >
          <VReviewPaymentSystemInfo
            :data="data?.payment_change_form"
            :locale-key="localeKey"
            :is-reviewed="isReviewed"
          />
        </VLoader>
      </div>
    </template>
    <template #leftSideTitle>
      <div class="flex min-h-10 items-center gap-2">
        <span class="flex-none">{{ $getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'REVIEW_MODAL_LEFT_COLUMN_TITLE') }}</span>
        <VButton
          v-if="!isReviewed && !isEditableMode && $hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.EDIT_PAYMENT_METHOD)"
          :label="$getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'FORM_MAKE_FORM_EDITABLE_BUTTON')"
          size="small"
          icon="pi-pencil"
          text
          truncate-label
          @click="setEditableMode(true)"
        />
      </div>
    </template>
    <template #leftSide>
      <VLoader :loading="isLoading">
        <VForm
          ref="paymentSettingsForm"
          :schema="newPaymentSystemFormSchema"
          :readonly="isReviewed || !isEditableMode || !$hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.EDIT_PAYMENT_METHOD)"
          :is-loading="isSaveLoading"
          :custom-errors="errors"
          @interacted="clearError"
          @submit="onSave"
          @change="onChange"
        />
      </VLoader>
    </template>
    <template #rightSideTitle>
      <div class="flex min-h-10 items-center">
        {{ $getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'REVIEW_MODAL_RIGHT_COLUMN_TITLE') }}
      </div>
    </template>
    <template #rightSide>
      <VLoader :loading="isLoading">
        <div>
          <div v-if="oldPaymentMethodName">
            <VForm
              :schema="oldPaymentSystemFormSchema"
              readonly
            />
          </div>
          <div v-else>
            <VMessage>
              {{ $getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'THERE_IS_NO_OLD_PAYMENT_SYSTEM_DETAILS') }}
            </VMessage>
          </div>
        </div>
      </VLoader>
    </template>
    <template #footer>
      <VLoader :loading="isLoading">
        <div
          v-if="!isReviewed"
          class="flex gap-2"
        >
          <div
            v-if="isEditableMode && $hasPermission(PAYMENT_SYSTEM_DETAILS_PERMISSIONS.EDIT_PAYMENT_METHOD)"
            class="flex gap-2"
          >
            <VButton
              :label="$getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'FORM_SAVE_BUTTON')"
              :loading="isSaveLoading"
              :disabled="isSingleConfirmLoading"
              @click="onSubmit"
            />
            <VButton
              :label="$getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'FORM_CANCEL_CHANGES_BUTTON')"
              outlined
              @click="setEditableMode(false)"
            />
          </div>
          <VReviewPaymentSystemButtons
            v-if="!isEditableMode"
            :id="paymentSystemId"
            :disabled="isSaveLoading"
            :is-loading="isSingleConfirmLoading"
            @approve="onConfirmSingle($event, EReviewPaymentSystemActionType.APPROVE)"
            @reject="onConfirmSingle($event, EReviewPaymentSystemActionType.REJECT)"
          />
        </div>
      </VLoader>
    </template>
  </AffiliatePaymentSystemDialog>
</template>
